import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './GoogleCallback.css';

export default function GoogleCallback() {
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('Completing sign-in...');
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');

    if (errorParam) {
      setError(`Google sign-in was cancelled or failed: ${errorParam}`);
      return;
    }

    if (!code) {
      setError('No authorization code received from Google.');
      return;
    }

    const handleCallback = async () => {
      try {
        setStatus('Verifying your account...');

        // Exchange the code for tokens
        const response = await fetch('/api/auth/google/callback', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || data.message || 'Authentication failed');
        }

        const data = await response.json();

        setStatus('Setting up your session...');

        // Log the user in
        login(data.token, data.user, data.completedSurveys || [], data.availableSurvey);

        // Get redirect destination
        const redirect = sessionStorage.getItem('auth_redirect') || '/';
        sessionStorage.removeItem('auth_redirect');

        // Staff and admin users don't need grade selection - go straight to home
        const isStaffOrAdmin = data.user.role === 'staff' || data.user.role === 'admin' || data.user.role === 'super-admin';

        // Check if grade needs to be selected (low confidence detection) - only for students
        if (!isStaffOrAdmin && data.user.gradeDetectionConfidence === 'low') {
          navigate('/select-grade', { replace: true });
        } else {
          navigate(redirect, { replace: true });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred during sign-in');
      }
    };

    handleCallback();
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <div className="callback-container">
        <div className="callback-card error">
          <div className="callback-icon error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h2>Sign-in Failed</h2>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => navigate('/login', { replace: true })}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="callback-container">
      <div className="callback-card">
        <div className="callback-spinner" />
        <h2>{status}</h2>
        <p>Please wait while we complete your sign-in.</p>
      </div>
    </div>
  );
}
